import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import Table from "../shared/table/Table";
import { toast } from "react-toastify";
import { format } from "date-fns";
import { tr } from "date-fns/locale";
import { FiEye } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import useTranslations from "../../hooks/useTranslations";
import useUserLanguage from "../../hooks/useUserLanguage";

const API = `${process.env.REACT_APP_API_BASE_URL}/api/product-reviews`;

const ProductReviewTable = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const language = useUserLanguage();
  const { translations } = useTranslations(language);
  const getText = (key, def) => translations[key] || def;

  const token = localStorage.getItem("admin_token");

  /* ---------- DATA FETCH ---------- */
  const fetchData = async () => {
    try {
      setLoading(true);

      const res = await axios.get(API, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setRows(res.data);
    } catch (err) {
      toast.error(getText("DATA_FETCH_ERROR"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  /* ---------- SWITCH ---------- */
  const Switch = ({ checked, onChange }) => (
    <div className="form-check form-switch text-center">
      <input
        className="form-check-input"
        type="checkbox"
        checked={Boolean(checked)}
        onChange={onChange}
        style={{ cursor: "pointer", width: 45, height: 22 }}
      />
    </div>
  );

  /* ---------- STATUS TOGGLE ---------- */
  const toggleStatus = async (id, current) => {
    try {
      await axios.put(
        `${API}/status/${id}`,
        { status: current ? 0 : 1 },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(getText("STATUS_UPDATED_SUCCESS"));
      fetchData();
    } catch {
      toast.error(getText("STATUS_UPDATED_ERROR"));
    }
  };

  /* ---------- APPROVE TOGGLE ---------- */
  const toggleApprove = async (id, current) => {
    try {
      await axios.put(
        `${API}/approve/${id}`,
        { is_approved: !Boolean(current) },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(
        getText("APPROVAL_UPDATED_SUCCESS")
      );
      fetchData();
    } catch {
      toast.error(
        getText("APPROVAL_UPDATED_ERROR")
      );
    }
  };

  const formatDate = (date) =>
    date ? format(new Date(date), "dd.MM.yyyy HH:mm", { locale: tr }) : "-";

  /* ---------- TABLE COLUMNS ---------- */
  const columns = useMemo(
    () => [
      {
        accessorKey: "id",
        header: getText("TABLE_ID_LABEL"),
        size: 60,
      },
      {
        accessorKey: "title",
        header: getText("TABLE_TITLE_LABEL"),
      },
      {
        accessorKey: "rating",
        header: getText("TABLE_RATING_LABEL"),
        cell: ({ row }) => (
          <span>
           
            <span className="text-DARK ms-1">
              ({row.original.rating}/5)
            </span>
          </span>
        ),
      },
      {
        accessorKey: "created_at",
        header: getText("LABEL_CREATED_AT"),
        cell: ({ row }) => formatDate(row.original.created_at),
      },
      {
        accessorKey: "approved_at",
        header: getText("TABLE_APPROVAL_DATE_LABEL"),
        cell: ({ row }) => formatDate(row.original.approved_at),
      },
      {
        accessorKey: "approvedBy",
        header: getText("TABLE_APPROVAL_BY_LABEL"),
        cell: ({ row }) =>
          row.original.approvedBy
            ? `${row.original.approvedBy.first_name} ${row.original.approvedBy.last_name}`
            : "-",
      },
      {
        accessorKey: "is_approved",
        header: getText("TABLE_APPROVAL_LABEL"),
        cell: ({ row }) => (
          <Switch
            checked={row.original.is_approved}
            onChange={() =>
              toggleApprove(row.original.id, row.original.is_approved)
            }
          />
        ),
      },
      {
        accessorKey: "status",
        header: getText("TABLE_STATUS_LABEL"),
        cell: ({ row }) => (
          <Switch
            checked={row.original.status}
            onChange={() =>
              toggleStatus(row.original.id, row.original.status)
            }
          />
        ),
      },
      {
        accessorKey: "actions",
        header: getText("TABLE_ACTIONS_LABEL"),
        cell: ({ row }) => (
          <button
            type="button"
            className="avatar-text avatar-md text-primary bg-soft-primary border-soft-primary"
            onClick={() => navigate(`/comment/view/${row.original.id}`)}
            title={getText("VIEW")}
          >
            <FiEye />
          </button>
        ),
      },
    ],
    [translations]
  );

  return (
    <Table
      data={rows}
      columns={columns}
      loading={loading}
    />
  );
};

export default ProductReviewTable;
